// Contact log view helpers: URL filters, day groups and entry wording. The backend filters,
// counts and marks each player's latest contact; this file only names and formats.
import { daysAgo, formatDay, formatShortDay, formatWeekday, PAGE_SIZE } from './trackerView';
import { daysUntil } from './queueView';

export const LOG_SCOPES = [
  { key: 'agent', label: 'This manager' },
  { key: 'all', label: 'All managers' },
];

// LOG_RANGES: contacted in the last N days (0: all time), or a custom From-To day range.
export const CUSTOM_RANGE = 'custom';
export const LOG_RANGES = [
  { key: 7, label: '7 days' },
  { key: 30, label: '30 days' },
  { key: 0, label: 'All time' },
  { key: CUSTOM_RANGE, label: 'Custom' },
];

const DEFAULT_DAYS = 30;
export const LONG_NOTE_CHARS = 140;

const positiveInt = (raw) => {
  const n = Number(raw);
  return Number.isInteger(n) && n > 0 ? n : 0;
};

const DAY = /^\d{4}-\d{2}-\d{2}$/;
const dayOrEmpty = (raw) => (DAY.test(raw || '') ? raw : '');

const daysFrom = (raw) => {
  if (raw === null) return DEFAULT_DAYS;
  if (raw === CUSTOM_RANGE) return CUSTOM_RANGE;
  const n = Number(raw);
  return LOG_RANGES.some((r) => r.key === n) ? n : DEFAULT_DAYS;
};

// logFiltersFromSearch reads the log filters from the URL
// (?tab=log&scope=&days=7|30|0|custom&from=&to=&channel=&player=&page=); from/to only count for custom.
export const logFiltersFromSearch = (searchParams) => {
  const days = daysFrom(searchParams.get('days'));
  const custom = days === CUSTOM_RANGE;
  return {
    scope: searchParams.get('scope') === 'all' ? 'all' : 'agent',
    days,
    from: custom ? dayOrEmpty(searchParams.get('from')) : '',
    to: custom ? dayOrEmpty(searchParams.get('to')) : '',
    channel: positiveInt(searchParams.get('channel')),
    player: positiveInt(searchParams.get('player')),
    page: positiveInt(searchParams.get('page')) || 1,
  };
};

export const searchWithLogFilters = ({ scope, days, from, to, channel, player, page }) => {
  const out = new URLSearchParams({ tab: 'log' });
  if (scope === 'all') out.set('scope', 'all');
  if (days !== DEFAULT_DAYS) out.set('days', String(days));
  if (days === CUSTOM_RANGE && from) out.set('from', from);
  if (days === CUSTOM_RANGE && to) out.set('to', to);
  if (channel) out.set('channel', String(channel));
  if (player) out.set('player', String(player));
  if (page > 1) out.set('page', String(page));
  return out;
};

// playerLogPath: the contact log filtered to one player, every manager, all time.
export const playerLogPath = (userId) => `/contact-tracker?${searchWithLogFilters({
  scope: 'all', days: 0, from: '', to: '', channel: 0, player: userId, page: 1,
})}`;

// logQuery: "This manager" without a chosen manager falls back to every manager's contacts.
// A reversed custom range is swapped rather than rejected.
export const logQuery = ({ scope, days, from, to, channel, player, page }, agent, search) => {
  const params = { page, page_size: PAGE_SIZE };
  if (scope !== 'all' && agent) params.agent = agent;
  if (days === CUSTOM_RANGE) {
    const [a, b] = from && to && from > to ? [to, from] : [from, to];
    if (a) params.from = a;
    if (b) params.to = b;
  } else if (days) params.days = days;
  if (channel) params.channel_id = channel;
  if (player) params.user_id = player;
  const q = (search || '').trim();
  if (q) params.search = q;
  return params;
};

// rangeText: the tile's range wording ("last 30 days", "1 Oct – 7 Oct", "from 1 Oct", "all time").
export const rangeText = ({ days, from, to }) => {
  if (days !== CUSTOM_RANGE) return days ? `last ${days} days` : 'all time';
  const [a, b] = from && to && from > to ? [to, from] : [from, to];
  const day = (ymd) => formatShortDay(`${ymd}T00:00:00`);
  if (a && b) return a === b ? day(a) : `${day(a)} – ${day(b)}`;
  if (a) return `from ${day(a)}`;
  if (b) return `until ${day(b)}`;
  return 'all time';
};

// isBackdated: the contact was logged on a later day than it happened.
export const isBackdated = (entry) => {
  const contacted = formatDay(entry?.contacted_at);
  const created = formatDay(entry?.created_at);
  return contacted !== '—' && created !== '—' && created > contacted;
};

// formatLoggedAt: when the entry was written, "27 Oct · 09:14 UTC" (created_at is zoneless UTC).
export const formatLoggedAt = (iso) => {
  const s = String(iso || '');
  const t = Date.parse(/Z$|[+-]\d{2}:?\d{2}$/.test(s) ? s : `${s}Z`);
  if (!iso || Number.isNaN(t)) return '—';
  return `${formatShortDay(iso)} · ${new Date(t).toISOString().slice(11, 16)} UTC`;
};

const contactsLabel = (n) => `${n} contact${n === 1 ? '' : 's'}`;

// dayLabel: "Today · Tue 27 Oct", "Yesterday · Mon 26 Oct" or "Sat 17 Oct".
export const dayLabel = (ymd, now = new Date()) => {
  const n = daysAgo(`${ymd}T00:00:00`, now);
  const day = formatWeekday(ymd);
  if (n === 0) return `Today · ${day}`;
  if (n === 1) return `Yesterday · ${day}`;
  return day;
};

// groupByDay splits the page (newest first) into contact days. sub counts the page's entries.
export const groupByDay = (entries, now = new Date()) => entries.reduce((groups, e) => {
  const day = formatDay(e.contacted_at);
  const last = groups[groups.length - 1];
  if (last && last.day === day) {
    const items = [...last.items, e];
    return [...groups.slice(0, -1), { ...last, items, sub: contactsLabel(items.length) }];
  }
  return [...groups, { day, label: dayLabel(day, now), items: [e], sub: contactsLabel(1) }];
}, []);

// nextContactCell: the follow-up pill. Only a player's latest contact still sets their follow-up.
export const nextContactCell = (entry, now = new Date()) => {
  const next = entry.next_contact_at;
  if (!next) return { label: 'No follow-up', tone: 'none', sub: '' };
  if (!entry.is_latest) return { label: formatShortDay(next), tone: 'done', sub: 'replaced by a later contact' };
  const ahead = daysUntil(next, now);
  if (ahead > 0) return { label: `In ${ahead}d`, tone: 'info', sub: formatShortDay(next) };
  const late = daysAgo(next, now);
  if (late > 0) return { label: `${late}d overdue`, tone: 'negative', sub: `was ${formatShortDay(next)}` };
  return { label: 'Due today', tone: 'warning', sub: formatShortDay(next) };
};

// CHANNEL_DOTS: a colour per known channel; others are dim.
const CHANNEL_DOTS = {
  Email: 'bg-brand-blue', Telegram: 'bg-positive', Call: 'bg-warning', WhatsApp: 'bg-brand-purple', 'Live chat': 'bg-negative-light',
};
export const channelDot = (channel) => CHANNEL_DOTS[channel] || 'bg-ink-dim';

// channelBars: the summary's channel counts as shares of all matching contacts.
export const channelBars = (summary) => {
  const total = summary?.contacts || 0;
  return (summary?.channels || []).map((c) => ({
    label: c.channel, n: c.contacts, dot: channelDot(c.channel), pct: total ? (c.contacts / total) * 100 : 0,
  }));
};

export const loggedBy = (email) => String(email || '').split('@')[0];

export const entryName = (e) => e.nickname || e.email || `Player ${e.user_id}`;

// playerChipName: the filtered player's name from the listed entries, else their ID.
export const playerChipName = (player, entries) => {
  const hit = (entries || []).find((e) => e.user_id === player);
  return hit ? entryName(hit) : `#${player}`;
};
